import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getUnreadSummary } from '@/lib/messaging/messaging-client';
import { totalUnread } from '@/types/messaging';
import { useAuth } from '@/contexts/AuthContext';

const FOCUSED_INTERVAL_MS = 30_000;
const HIDDEN_INTERVAL_MS = 120_000;

export const unreadSummaryQueryKey = ['messaging', 'unread-summary'] as const;

export const useUnreadSummary = () => {
  const { profile } = useAuth();
  const profileId = profile?.id;

  const [hidden, setHidden] = useState<boolean>(
    typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false,
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVis = () => setHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const refetchInterval = hidden ? HIDDEN_INTERVAL_MS : FOCUSED_INTERVAL_MS;

  const query = useQuery({
    queryKey: unreadSummaryQueryKey,
    enabled: !!profileId,
    refetchInterval,
    queryFn: async () => {
      const { data, error } = await getUnreadSummary();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  return {
    ...query,
    totalUnreadCount: totalUnread(query.data),
  };
};
